import React, { useEffect, useState, useMemo, useRef } from "react";
import { Button, Tag, Tooltip, Table, Input, Dropdown, Space, Row, Col } from "antd";
import {
  SearchOutlined,
  SettingOutlined,
  ReloadOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import theme from "../theme";
import {
  getIntegrations,
  getCapabilityList,
  getDetail,
  getConfig,
  saveSchemaConfig,
  reloadSchema,
} from "./schema-manage-service";
import useApiCall from "../common/api-call";
import { useToast } from "../common/toast";
import LoadingSpinner from "../common/loading";
import SchemaConfigDialog from "./schema-config-dialog";

const EllipsisTooltipCell = ({ value, width = 150 }) => {
  return (
    <Tooltip
      title={value}
      placement="topLeft"
      color={theme.palette.background.card}
      overlayInnerStyle={{
        color: theme.palette.text.primary,
      }}
    >
      <div
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          maxWidth: width,
        }}
      >
        {value}
      </div>
    </Tooltip>
  );
};

const SchemaManageView = () => {
  const { apiCall } = useApiCall();
  const { errorToast, successToast } = useToast();

  const [integrations, setIntegrations] = useState([]);
  const [capabilities, setCapabilities] = useState([]);
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [selectedCapability, setSelectedCapability] = useState(null);
  const [integrationsPageSize, setIntegrationsPageSize] = useState(20);
  const [capabilitiesPageSize, setCapabilitiesPageSize] = useState(20);
  const [integrationsDensity, setIntegrationsDensity] = useState("small");
  const [capabilitiesDensity, setCapabilitiesDensity] = useState("small");
  const [integrationsColumnVisibility, setIntegrationsColumnVisibility] = useState({
    status: true,
    latest: true,
    actions: true,
  });
  const [capabilitiesColumnVisibility, setCapabilitiesColumnVisibility] = useState({
    status: true,
    latest: true,
    actions: true,
  });

  const [loading, setLoading] = useState(false);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [prodConfig, setProdConfig] = useState({ latest: "", inUse: "" });
  const [stagingConfig, setStagingConfig] = useState({ latest: "", inUse: "" });
  const [currentDialogSchema, setCurrentDialogSchema] = useState(null);
  const [details, setDetails] = useState([]);

  useEffect(() => {
    fetchIntegrations();
  }, []);

  useEffect(() => {
    if (integrations.length > 0) {
      const firstIntegration = integrations[0];
      setSelectedIntegration(firstIntegration);
      fetchCapabilities(firstIntegration.id);
    }
  }, [integrations]);

  useEffect(() => {
    setSelectedCapability(null);
  }, [capabilities]);

  // Add scroll listeners to show scrollbars when scrolling
  useEffect(() => {
    const handleScroll = (e) => {
      const target = e.target;
      if (target.classList.contains("ant-table-body")) {
        target.classList.add("scrolling");
        clearTimeout(target.scrollTimeout);
        target.scrollTimeout = setTimeout(() => {
          target.classList.remove("scrolling");
        }, 500); // Hide scrollbar 500ms after scrolling stops
      }
    };

    // Use a MutationObserver to watch for table body elements
    const observer = new MutationObserver(() => {
      const tableBodies = document.querySelectorAll(".ant-table-body");
      tableBodies.forEach((body) => {
        if (!body.hasScrollListener) {
          body.addEventListener("scroll", handleScroll, { passive: true });
          body.hasScrollListener = true;
        }
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    // Initial check for existing table bodies
    const tableBodies = document.querySelectorAll(".ant-table-body");
    tableBodies.forEach((body) => {
      if (!body.hasScrollListener) {
        body.addEventListener("scroll", handleScroll, { passive: true });
        body.hasScrollListener = true;
      }
    });

    return () => {
      observer.disconnect();
      const tableBodies = document.querySelectorAll(".ant-table-body");
      tableBodies.forEach((body) => {
        if (body.hasScrollListener) {
          body.removeEventListener("scroll", handleScroll);
          body.hasScrollListener = false;
          if (body.scrollTimeout) {
            clearTimeout(body.scrollTimeout);
          }
        }
      });
    };
  }, [loading]);

  const fetchIntegrations = async () => {
    try {
      setLoading(true);
      const data = await getIntegrations(apiCall);

      const processedIntegrations = data.schemas.map((integration, index) => ({
        id: integration.id,
        isProd: integration.prodV > 0,
        isDraft: integration.draftV > 0,
        isActive: integration.activeV > 0,
        isLatest:
          integration.activeV ===
          (integration.draftV > 0 ? integration.draftV : integration.prodV),
      }));
      setIntegrations(processedIntegrations);
      setLoading(false);
    } catch (error) {
      errorToast("Failed to fetch integrations");
      setLoading(false);
    }
  };

  const fetchCapabilities = async integrationId => {
    try {
      const data = await getCapabilityList(apiCall, integrationId);
      const processedCapabilities = data.schemas.map((capability, index) => ({
        id: capability.id,
        key: `${integrationId}-${capability.id}`,
        isProd: capability.prodV > 0,
        isDraft: capability.draftV > 0,
        isActive: capability.activeV > 0,
        isLatest:
          capability.activeV ===
          (capability.draftV > 0 ? capability.draftV : capability.prodV),
        integrationId: integrationId,
      }));
      setCapabilities(processedCapabilities);
    } catch (error) {
      errorToast("Failed to fetch capabilities");
    }
  };

  const handleIntegrationRowClick = params => {
    setSelectedIntegration(params.row);
    fetchCapabilities(params.row.id);
  };

  const handleCapabilityRowClick = params => {
    setSelectedCapability(params.row);
  };

  const handleSaveConfig = async envTab => {
    try {
      if (!currentDialogSchema) {
        errorToast("No configuration selected");
        return;
      }

      const configToSave =
        envTab === 0 ? prodConfig.latest : stagingConfig.latest;
      const env = envTab === 0 ? "PROD" : "STAGING";

      const resp = await saveSchemaConfig(apiCall, configToSave, env);
      if (resp && resp.saved) {
        successToast("Configuration saved successfully");
      } else {
        errorToast("Failed to save configuration");
      }

      setConfigDialogOpen(false);
    } catch (error) {
      errorToast("Failed to save configuration");
    }
  };

  const handleSaveScript = async () => {};

  const handleShowConfig = async schema => {
    try {
      const [prodDetail, stagingDetail] = await Promise.all([
        getConfig(apiCall, schema.id, "PROD"),
        getConfig(apiCall, schema.id, "STAGING"),
      ]);

      setProdConfig({
        latest: prodDetail.latest || "",
        inUse: prodDetail.inUse || "",
      });
      setStagingConfig({
        latest: stagingDetail.latest || "",
        inUse: stagingDetail.inUse || "",
      });

      setCurrentDialogSchema({ ...schema, type: "config" });
      setConfigDialogOpen(true);
    } catch (error) {
      errorToast("Failed to fetch schema details");
    }
  };

  const handleReloadSchemas = async (rebuild = false, env = "STAGING") => {
    try {
      const result = await reloadSchema(apiCall, rebuild, env);
      if (result.success) {
        successToast(
          `Schemas ${rebuild ? `rebuilt in ${env}` : "reloaded"} successfully`,
        );
        fetchIntegrations();
        fetchCapabilities(selectedIntegration?.id);
      } else {
        errorToast(
          `Failed to ${rebuild ? `rebuild in ${env}` : "reload"} schemas`,
        );
      }
    } catch (error) {
      errorToast(
        `Unexpected error during schema ${
          rebuild ? `rebuild in ${env}` : "reload"
        }`,
      );
    }
  };

  const handleShowScript = async schema => {
    try {
      const fetchedDetails = await getDetail(apiCall, schema.id);

      setDetails(fetchedDetails);
      setCurrentDialogSchema({ ...schema, type: "script" });
      setConfigDialogOpen(true);
    } catch (error) {
      errorToast("Failed to fetch schema details");
    }
  };

  const handleExportIntegrations = () => {
    const csvContent = [
      ["Integration", "Prod", "Draft", "Active", "Latest"].join(","),
      ...integrations.map(row =>
        [
          `"${row.id}"`,
          row.isProd ? "Yes" : "No",
          row.isDraft ? "Yes" : "No",
          row.isActive ? "Yes" : "No",
          row.isLatest ? "Yes" : "No",
        ].join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `integrations_${new Date().toISOString().split("T")[0]}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    successToast("Integrations exported successfully");
  };

  const handleExportCapabilities = () => {
    const csvContent = [
      ["Capability", "Prod", "Draft", "Active", "Latest"].join(","),
      ...capabilities.map(row =>
        [
          `"${row.id}"`,
          row.isProd ? "Yes" : "No",
          row.isDraft ? "Yes" : "No",
          row.isActive ? "Yes" : "No",
          row.isLatest ? "Yes" : "No",
        ].join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `capabilities_${new Date().toISOString().split("T")[0]}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    successToast("Capabilities exported successfully");
  };

  const integrationsColumnSettingsMenu = {
    items: [
      {
        key: "status",
        label: (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input
              type="checkbox"
              checked={integrationsColumnVisibility.status}
              onChange={(e) =>
                setIntegrationsColumnVisibility({
                  ...integrationsColumnVisibility,
                  status: e.target.checked,
                })
              }
            />
            Status
          </div>
        ),
      },
      {
        key: "latest",
        label: (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input
              type="checkbox"
              checked={integrationsColumnVisibility.latest}
              onChange={(e) =>
                setIntegrationsColumnVisibility({
                  ...integrationsColumnVisibility,
                  latest: e.target.checked,
                })
              }
            />
            Latest
          </div>
        ),
      },
      {
        key: "actions",
        label: (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input
              type="checkbox"
              checked={integrationsColumnVisibility.actions}
              onChange={(e) =>
                setIntegrationsColumnVisibility({
                  ...integrationsColumnVisibility,
                  actions: e.target.checked,
                })
              }
            />
            Actions
          </div>
        ),
      },
      { type: "divider" },
      {
        key: "density",
        label: "Density",
        children: [
          {
            key: "small",
            label: (
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input
                  type="radio"
                  name="integrations-density"
                  checked={integrationsDensity === "small"}
                  onChange={() => setIntegrationsDensity("small")}
                />
                Compact
              </div>
            ),
          },
          {
            key: "middle",
            label: (
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input
                  type="radio"
                  name="integrations-density"
                  checked={integrationsDensity === "middle"}
                  onChange={() => setIntegrationsDensity("middle")}
                />
                Middle
              </div>
            ),
          },
          {
            key: "large",
            label: (
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input
                  type="radio"
                  name="integrations-density"
                  checked={integrationsDensity === "large"}
                  onChange={() => setIntegrationsDensity("large")}
                />
                Comfortable
              </div>
            ),
          },
        ],
      },
    ],
  };

  const capabilitiesColumnSettingsMenu = {
    items: [
      {
        key: "status",
        label: (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input
              type="checkbox"
              checked={capabilitiesColumnVisibility.status}
              onChange={(e) =>
                setCapabilitiesColumnVisibility({
                  ...capabilitiesColumnVisibility,
                  status: e.target.checked,
                })
              }
            />
            Status
          </div>
        ),
      },
      {
        key: "latest",
        label: (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input
              type="checkbox"
              checked={capabilitiesColumnVisibility.latest}
              onChange={(e) =>
                setCapabilitiesColumnVisibility({
                  ...capabilitiesColumnVisibility,
                  latest: e.target.checked,
                })
              }
            />
            Latest
          </div>
        ),
      },
      {
        key: "actions",
        label: (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input
              type="checkbox"
              checked={capabilitiesColumnVisibility.actions}
              onChange={(e) =>
                setCapabilitiesColumnVisibility({
                  ...capabilitiesColumnVisibility,
                  actions: e.target.checked,
                })
              }
            />
            Actions
          </div>
        ),
      },
      { type: "divider" },
      {
        key: "density",
        label: "Density",
        children: [
          {
            key: "small",
            label: (
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input
                  type="radio"
                  name="capabilities-density"
                  checked={capabilitiesDensity === "small"}
                  onChange={() => setCapabilitiesDensity("small")}
                />
                Compact
              </div>
            ),
          },
          {
            key: "middle",
            label: (
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input
                  type="radio"
                  name="capabilities-density"
                  checked={capabilitiesDensity === "middle"}
                  onChange={() => setCapabilitiesDensity("middle")}
                />
                Middle
              </div>
            ),
          },
          {
            key: "large",
            label: (
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input
                  type="radio"
                  name="capabilities-density"
                  checked={capabilitiesDensity === "large"}
                  onChange={() => setCapabilitiesDensity("large")}
                />
                Comfortable
              </div>
            ),
          },
        ],
      },
    ],
  };

  const integrationsColumns = [
    {
      title: "Integration",
      dataIndex: "id",
      width: 150,
      ellipsis: {
        showTitle: false,
      },
      sorter: (a, b) => a.id.localeCompare(b.id),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Search integration"
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => confirm()}
            style={{ marginBottom: 8, display: "block", height: "40px", fontSize: "14px" }}
          />
          <div style={{ display: "flex", gap: 8 }}>
            <Button
              type="primary"
              onClick={() => confirm()}
              size="small"
              style={{ width: 90 }}
            >
              Search
            </Button>
            <Button
              onClick={() => {
                clearFilters();
                confirm();
              }}
              size="small"
              style={{ width: 90 }}
            >
              Reset
            </Button>
          </div>
        </div>
      ),
      filterIcon: (filtered) => (
        <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
      ),
      onFilter: (value, record) =>
        record.id.toLowerCase().includes(value.toLowerCase()),
      render: (text) => <EllipsisTooltipCell value={text} width={150} />,
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 200,
      filters: [
        { text: "Prod", value: "prod" },
        { text: "Draft", value: "draft" },
        { text: "Active", value: "active" },
      ],
      onFilter: (value, record) => {
        if (value === "prod") return record.isProd;
        if (value === "draft") return record.isDraft;
        if (value === "active") return record.isActive;
        return true;
      },
      render: (_, record) => (
        <Space size="small">
          <Tag
            color={record.isProd ? undefined : "default"}
            style={
              record.isProd
                ? {
                    backgroundColor: "#4caf50",
                    color: theme.palette.text.white,
                    borderColor: "#4caf50",
                    borderRadius: "8px",
                  }
                : { borderRadius: "8px" }
            }
          >
            Prod
          </Tag>
          <Tag
            color={record.isDraft ? undefined : "default"}
            style={
              record.isDraft
                ? {
                    backgroundColor: "#ff9800",
                    color: theme.palette.text.white,
                    borderColor: "#ff9800",
                    borderRadius: "8px",
                  }
                : { borderRadius: "8px" }
            }
          >
            Draft
          </Tag>
          <Tag
            color={record.isActive ? undefined : "default"}
            style={
              record.isActive
                ? {
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.text.white,
                    borderColor: theme.palette.primary.main,
                    borderRadius: "8px",
                  }
                : { borderRadius: "8px" }
            }
          >
            Active
          </Tag>
        </Space>
      ),
    },
    {
      title: "Latest",
      dataIndex: "latest",
      filters: [
        { text: "Yes", value: true },
        { text: "No", value: false },
      ],
      onFilter: (value, record) => record.isLatest === value,
      render: (_, record) => (
        <Tag
          color={record.isLatest ? undefined : "default"}
          style={
            record.isLatest
              ? {
                  backgroundColor: "#4caf50",
                  color: theme.palette.text.white,
                  borderColor: "#4caf50",
                  borderRadius: "8px",
                }
              : { borderRadius: "8px" }
          }
        >
          {record.isLatest ? "Yes" : "No"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      dataIndex: "actions",
      width: 160,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="primary"
            size="small"
            onClick={e => {
              e.stopPropagation();
              handleShowConfig(record);
            }}
          >
            Config
          </Button>
          <Button
            type="primary"
            size="small"
            onClick={e => {
              e.stopPropagation();
              e.preventDefault();
              handleShowScript(record);
            }}
          >
            Script
          </Button>
        </Space>
      ),
    },
  ];

  const capabilitiesColumns = [
    {
      title: "Capability",
      dataIndex: "id",
      width: 200,
      ellipsis: {
        showTitle: false,
      },
      sorter: (a, b) => a.id.localeCompare(b.id),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Search capability"
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => confirm()}
            style={{ marginBottom: 8, display: "block", height: "40px", fontSize: "14px" }}
          />
          <div style={{ display: "flex", gap: 8 }}>
            <Button
              type="primary"
              onClick={() => confirm()}
              size="small"
              style={{ width: 90 }}
            >
              Search
            </Button>
            <Button
              onClick={() => {
                clearFilters();
                confirm();
              }}
              size="small"
              style={{ width: 90 }}
            >
              Reset
            </Button>
          </div>
        </div>
      ),
      filterIcon: (filtered) => (
        <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
      ),
      onFilter: (value, record) =>
        record.id.toLowerCase().includes(value.toLowerCase()),
      render: (text) => <EllipsisTooltipCell value={text} width={200} />,
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 200,
      filters: [
        { text: "Prod", value: "prod" },
        { text: "Draft", value: "draft" },
        { text: "Active", value: "active" },
      ],
      onFilter: (value, record) => {
        if (value === "prod") return record.isProd;
        if (value === "draft") return record.isDraft;
        if (value === "active") return record.isActive;
        return true;
      },
      render: (_, record) => (
        <Space size="small">
          <Tag
            color={record.isProd ? undefined : "default"}
            style={
              record.isProd
                ? {
                    backgroundColor: "#4caf50",
                    color: theme.palette.text.white,
                    borderColor: "#4caf50",
                    borderRadius: "8px",
                  }
                : { borderRadius: "8px" }
            }
          >
            Prod
          </Tag>
          <Tag
            color={record.isDraft ? undefined : "default"}
            style={
              record.isDraft
                ? {
                    backgroundColor: "#ff9800",
                    color: theme.palette.text.white,
                    borderColor: "#ff9800",
                    borderRadius: "8px",
                  }
                : { borderRadius: "8px" }
            }
          >
            Draft
          </Tag>
          <Tag
            color={record.isActive ? undefined : "default"}
            style={
              record.isActive
                ? {
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.text.white,
                    borderColor: theme.palette.primary.main,
                    borderRadius: "8px",
                  }
                : { borderRadius: "8px" }
            }
          >
            Active
          </Tag>
        </Space>
      ),
    },
    {
      title: "Latest",
      dataIndex: "latest",
      width: 100,
      filters: [
        { text: "Yes", value: true },
        { text: "No", value: false },
      ],
      onFilter: (value, record) => record.isLatest === value,
      render: (_, record) => (
        <Tag
          color={record.isLatest ? undefined : "default"}
          style={
            record.isLatest
              ? {
                  backgroundColor: "#4caf50",
                  color: theme.palette.text.white,
                  borderColor: "#4caf50",
                  borderRadius: "8px",
                }
              : { borderRadius: "8px" }
          }
        >
          {record.isLatest ? "Yes" : "No"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      dataIndex: "actions",
      width: 100,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="primary"
            size="small"
            onClick={e => {
              handleShowScript(record);
            }}
          >
            Script
          </Button>
        </Space>
      ),
    },
  ];

  const filteredIntegrationsColumns = useMemo(() => {
    return integrationsColumns.filter(col => {
      if (col.dataIndex === "status") return integrationsColumnVisibility.status;
      if (col.dataIndex === "latest") return integrationsColumnVisibility.latest;
      if (col.dataIndex === "actions") return integrationsColumnVisibility.actions;
      return true;
    });
  }, [integrationsColumnVisibility, integrationsColumns]);

  const filteredCapabilitiesColumns = useMemo(() => {
    return capabilitiesColumns.filter(col => {
      if (col.dataIndex === "status") return capabilitiesColumnVisibility.status;
      if (col.dataIndex === "latest") return capabilitiesColumnVisibility.latest;
      if (col.dataIndex === "actions") return capabilitiesColumnVisibility.actions;
      return true;
    });
  }, [capabilitiesColumnVisibility, capabilitiesColumns]);

  return (
    <div style={{ flexGrow: 1, padding: 16 }}>
      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
          <LoadingSpinner />
        </div>
      ) : (
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Space wrap style={{ marginBottom: 16 }}>
            <Space size="small">
              <Button
                type="primary"
                onClick={() => handleReloadSchemas(false)}
              >
                Reload
              </Button>
            </Space>
            <Space size="small">
              <Button
                type="primary"
                onClick={() => handleReloadSchemas(true, "STAGING")}
              >
                Rebuild Staging
              </Button>
              <Button
                type="primary"
                onClick={() => handleReloadSchemas(true, "PROD")}
              >
                Rebuild Prod
              </Button>
            </Space>
          </Space>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Table
                dataSource={integrations}
                columns={filteredIntegrationsColumns}
                onRow={(record) => ({
                  onClick: () => handleIntegrationRowClick({ row: record }),
                  className: record === selectedIntegration ? "selected-row" : "",
                })}
                pagination={{
                  pageSize: integrationsPageSize,
                  pageSizeOptions: [5, 10, 20],
                  showSizeChanger: true,
                  onShowSizeChange: (current, size) => {
                    setIntegrationsPageSize(size);
                  },
                }}
                size={integrationsDensity}
                rowKey={(record) => record.key || record.id}
                style={{ border: "none" }}
                scroll={{ x: "max-content", y: "calc(100vh - 280px)" }}
              />
            </Col>
            <Col xs={24} md={12}>
              <Table
                dataSource={capabilities}
                columns={filteredCapabilitiesColumns}
                onRow={(record) => ({
                  onClick: () => handleCapabilityRowClick({ row: record }),
                  className: record === selectedCapability ? "selected-row" : "",
                })}
                pagination={{
                  pageSize: capabilitiesPageSize,
                  pageSizeOptions: [10, 20, 50],
                  showSizeChanger: true,
                  onShowSizeChange: (current, size) => {
                    setCapabilitiesPageSize(size);
                  },
                }}
                size={capabilitiesDensity}
                rowKey={(record) => record.key || record.id}
                style={{ border: "none" }}
                scroll={{ x: "max-content", y: "calc(100vh - 280px)" }}
              />
            </Col>
          </Row>
        </Space>
      )}

      <SchemaConfigDialog
        open={configDialogOpen}
        onClose={() => setConfigDialogOpen(false)}
        currentSchema={currentDialogSchema}
        onSaveConfig={handleSaveConfig}
        onSaveScript={handleSaveScript}
        prodConfig={prodConfig}
        setProdConfig={setProdConfig}
        stagingConfig={stagingConfig}
        setStagingConfig={setStagingConfig}
        details={details}
        setDetails={setDetails}
      />
    </div>
  );
};

export default SchemaManageView;
